import { useCallback, useMemo, useRef } from 'react';

import {
    useCheckQuota,
    useDisableStream,
    useStartStream,
    useStopStream,
} from '@/domain/videoStream';
import { logger } from '@/utils/logger';

interface VideoStreamApiParams {
    bioscopeId?: number;
}

interface VideoStreamApiMethods {
    startStream: () => Promise<void>;
    stopStream: () => Promise<void>;
    disableStream: () => Promise<void>;
    checkQuota: () => Promise<{ status: string } | undefined>;
}

interface VideoStreamApiReturn {
    api: VideoStreamApiMethods;
    isStarting: boolean;
    isStopping: boolean;
}

/**
 * Hook that provides stable API references for video stream operations.
 * Uses refs internally to avoid causing re-renders in consuming effects.
 * The `api` object is stable and won't change between renders.
 */
export function useVideoStreamApi({ bioscopeId }: VideoStreamApiParams): VideoStreamApiReturn {
    // DEBUG: Track renders
    const hookRenderCountRef = useRef(0);
    hookRenderCountRef.current += 1;
    logger.dev('useVideoStreamApi', 'Render #', hookRenderCountRef.current, { bioscopeId });

    const { mutateAsync: startStreamApi, isLoading: isStarting } = useStartStream();
    const { mutateAsync: stopStreamApi, isLoading: isStopping } = useStopStream();
    const { mutateAsync: disableStreamApi } = useDisableStream();
    const { mutateAsync: checkQuotaApi } = useCheckQuota();

    // DEBUG: Track when mutateAsync references change
    const prevStartStreamApiRef = useRef(startStreamApi);
    const prevStopStreamApiRef = useRef(stopStreamApi);
    const prevDisableStreamApiRef = useRef(disableStreamApi);
    const prevCheckQuotaApiRef = useRef(checkQuotaApi);

    if (prevStartStreamApiRef.current !== startStreamApi) {
        logger.dev(
            'useVideoStreamApi',
            'startStreamApi reference changed! isStarting:',
            isStarting
        );
        prevStartStreamApiRef.current = startStreamApi;
    }
    if (prevStopStreamApiRef.current !== stopStreamApi) {
        logger.dev('useVideoStreamApi', 'stopStreamApi reference changed! isStopping:', isStopping);
        prevStopStreamApiRef.current = stopStreamApi;
    }
    if (prevDisableStreamApiRef.current !== disableStreamApi) {
        logger.dev('useVideoStreamApi', 'disableStreamApi reference changed!');
        prevDisableStreamApiRef.current = disableStreamApi;
    }
    if (prevCheckQuotaApiRef.current !== checkQuotaApi) {
        logger.dev('useVideoStreamApi', 'checkQuotaApi reference changed!');
        prevCheckQuotaApiRef.current = checkQuotaApi;
    }

    // Keep bioscopeId in ref to use in stable callbacks
    const bioscopeIdRef = useRef(bioscopeId);
    bioscopeIdRef.current = bioscopeId;

    // Stable callbacks that won't change between renders
    const startStream = useCallback(async () => {
        const id = bioscopeIdRef.current;
        logger.dev('useVideoStreamApi', 'startStream called', { bioscopeId: id });
        if (!id) return;
        await startStreamApi({ bioscopeId: id });
    }, [startStreamApi]);

    const stopStream = useCallback(async () => {
        const id = bioscopeIdRef.current;
        logger.dev('useVideoStreamApi', 'stopStream called', { bioscopeId: id });
        if (!id) return;
        await stopStreamApi({ bioscopeId: id });
    }, [stopStreamApi]);

    const disableStream = useCallback(async () => {
        const id = bioscopeIdRef.current;
        logger.dev('useVideoStreamApi', 'disableStream called', { bioscopeId: id });
        if (!id) return;
        await disableStreamApi({ bioscopeId: id });
    }, [disableStreamApi]);

    const checkQuota = useCallback(async () => {
        const id = bioscopeIdRef.current;
        logger.dev('useVideoStreamApi', 'checkQuota called', { bioscopeId: id });
        if (!id) return undefined;
        return checkQuotaApi({ bioscopeId: id });
    }, [checkQuotaApi]);

    // DEBUG: Track when callback references change
    const prevStartStreamRef = useRef(startStream);
    const prevStopStreamRef = useRef(stopStream);
    const prevDisableStreamRef = useRef(disableStream);
    const prevCheckQuotaRef = useRef(checkQuota);

    if (prevStartStreamRef.current !== startStream) {
        logger.dev('useVideoStreamApi', 'startStream callback reference changed!');
        prevStartStreamRef.current = startStream;
    }
    if (prevStopStreamRef.current !== stopStream) {
        logger.dev('useVideoStreamApi', 'stopStream callback reference changed!');
        prevStopStreamRef.current = stopStream;
    }
    if (prevDisableStreamRef.current !== disableStream) {
        logger.dev('useVideoStreamApi', 'disableStream callback reference changed!');
        prevDisableStreamRef.current = disableStream;
    }
    if (prevCheckQuotaRef.current !== checkQuota) {
        logger.dev('useVideoStreamApi', 'checkQuota callback reference changed!');
        prevCheckQuotaRef.current = checkQuota;
    }

    // Memoize the api object to keep it stable
    // This prevents cleanup effects from running when loading states change
    const api = useMemo(
        () => ({
            startStream,
            stopStream,
            disableStream,
            checkQuota,
        }),
        [startStream, stopStream, disableStream, checkQuota]
    );

    // DEBUG: Track when api object reference changes
    const prevApiRef = useRef(api);
    if (prevApiRef.current !== api) {
        logger.dev('useVideoStreamApi', 'api object reference changed!');
        prevApiRef.current = api;
    }

    return {
        api,
        isStarting,
        isStopping,
    };
}
